import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { uniqBy } from 'lodash'

import { CommonTableColumnsEnum } from '../../components/Table/types'
import { SortDirectionCapital } from '../../types'
import { getInitialPaginationData, getInitialTableColumns } from '../../utils'

import { Trade, TradeAiAnalysis, TradingLogInitialState, TradingLogRequestType } from './types'

const initialState: TradingLogInitialState = {
  trades: [],
  pagination: {
    ...getInitialPaginationData<Trade>(getInitialTableColumns().tradingLog as (keyof Trade)[]),
    order: SortDirectionCapital.desc,
    orderBy: CommonTableColumnsEnum.openTime,
  },
  selectedAll: false,
  selectedTrades: [],
  massFillEnabled: false,
}

export const fetchTrades = createAsyncThunk(
  'tradingLog/fetchTrades',
  async (pagination: TradingLogRequestType) => {
    try {
      const { TradingLogService } = await import('./TradingLogService')
      const response = await TradingLogService.getTrades({ ...pagination, fromCalendar: false })
      return response.data
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchAllTradesInfo = createAsyncThunk(
  'tradingLog/fetchAllTradesInfo',
  async (account: number) => {
    try {
      const { TradingLogService } = await import('./TradingLogService')
      const response = await TradingLogService.fetchAllTradesInfo({ account })
      return response.data
    } catch (error) {
      throw Error(error.message)
    }
  },
)

const TradingLogSlice = createSlice({
  name: 'TradingLog',
  initialState,
  reducers: {
    updateColumnOrder: (state, action) => {
      state.pagination.columns = action.payload
    },
    updatePageSize: (state, action) => {
      state.pagination.perPage = action.payload
    },
    updatePage: (state, action) => {
      state.pagination.page = action.payload
    },
    updateOrderBy: (state, action) => {
      state.pagination.page = 1
      state.pagination.order = SortDirectionCapital.asc
      state.pagination.orderBy = action.payload
    },
    updateSortOrder: (state, action) => {
      state.pagination.page = 1
      state.pagination.order = action.payload
    },
    clearAllTradingLog: (state) => {
      state = initialState
    },
    selectAllTrades: (state) => {
      state.selectedAll = true
      state.trades = state.trades.map((trade) => ({ ...trade, selected: true }))
    },
    dismissAllTradesSelect: (state) => {
      state.selectedAll = false
      state.selectedTrades = []
      state.trades = state.trades.map((trade) => ({ ...trade, selected: false }))
    },
    addTradeToMassFill: (state, action) => {
      state.selectedTrades = [...state.selectedTrades, action.payload]
      state.trades = state.trades.map((trade) => {
        if (Number(trade.id) !== action.payload.id) return trade
        return { ...trade, selected: true }
      })

      const { itemCount } = state.pagination || {}
      if (itemCount === state.selectedTrades?.length) {
        state.selectedAll = true
      }
    },
    removeTradeFromMassFill: (state, action) => {
      state.selectedTrades = state.selectedTrades.filter(({ id }) => id !== action.payload.id)
      state.trades = state.trades.map((trade) => {
        if (Number(trade.id) !== action.payload.id) return trade
        return { ...trade, selected: false }
      })
      state.selectedAll = false
    },
    addAllTradesOnPageToMassFill: (state, action) => {
      state.selectedTrades = uniqBy([...state.selectedTrades, ...action.payload], 'id')
      state.trades = state.trades.map((trade) => {
        const isTradeSelected = action.payload.some(
          (payloadTrade: { id: number }) => payloadTrade.id === Number(trade.id),
        )
        if (!isTradeSelected) return trade
        return { ...trade, selected: true }
      })

      const { itemCount } = state.pagination || {}
      if (itemCount === state.selectedTrades?.length) {
        state.selectedAll = true
      }
    },
    removeAllTradesOnPageToMassFill: (state, action) => {
      state.selectedTrades = state.selectedTrades.filter(
        (item) =>
          !action.payload.find(
            (trade: { id: number; journalingCompletion: number }) => trade.id === item.id,
          ),
      )
      state.trades = state.trades.map((trade) => {
        const isTradeSelected = action.payload.some(
          (payloadTrade: { id: number }) => payloadTrade.id === Number(trade.id),
        )
        if (!isTradeSelected) return trade
        return { ...trade, selected: false }
      })
      state.selectedAll = false
    },
    enableMassFill: (state) => {
      state.massFillEnabled = true
    },
    disableMassFill: (state) => {
      state.massFillEnabled = false
    },
    setTradeAiAnalysis: (
      state,
      action: {
        payload: { tradeId: number; analysis: TradeAiAnalysis | null }
      },
    ) => {
      state.trades = state.trades.map((trade) =>
        Number(trade.id) === action.payload.tradeId
          ? { ...trade, tradeAiAnalysis: action.payload.analysis }
          : trade,
      )
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTrades.fulfilled, (state, action) => {
      state.trades = action.payload.data.map((item) => ({ ...item, selected: false }))
      state.pagination = {
        ...state.pagination,
        ...action.payload.meta,
      }
    })
    builder.addCase(fetchAllTradesInfo.fulfilled, (state, action) => {
      state.selectedTrades = action.payload
    })
  },
})

const TradingLogReducer = TradingLogSlice.reducer
export const {
  updateColumnOrder,
  updatePageSize,
  updatePage,
  updateOrderBy,
  updateSortOrder,
  clearAllTradingLog,
  selectAllTrades,
  dismissAllTradesSelect,
  addTradeToMassFill,
  removeTradeFromMassFill,
  addAllTradesOnPageToMassFill,
  removeAllTradesOnPageToMassFill,
  enableMassFill,
  disableMassFill,
  setTradeAiAnalysis,
} = TradingLogSlice.actions
export default TradingLogReducer
